import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";

export default function Navigation() {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const jump = (id) => {
        setOpen(false);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    };

    const items = [
        { l: t("nav.ritual"), id: "ritual" },
        { l: t("nav.benefits"), id: "benefits" },
        { l: t("nav.faq"), id: "faq" },
    ];

    return (
        <motion.header
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl ${
                scrolled
                    ? "bg-[#F9F6F0]/85 border-b border-[#D9D1C7]"
                    : "bg-transparent"
            }`}
            data-testid="site-nav"
        >
            <div className="container-x flex items-center justify-between py-5">
                <button
                    onClick={() => jump("top")}
                    className="font-serif text-2xl md:text-[26px] tracking-tight leading-none"
                    data-testid="nav-logo"
                >
                    VARDHAN<sup className="text-[10px] ml-0.5 text-[#B48F57]">™</sup>
                </button>

                <nav className="hidden md:flex items-center gap-10">
                    {items.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => jump(item.id)}
                            className="text-[13px] tracking-[0.18em] uppercase text-[#5C564D] hover:text-[#2D2824] transition-colors"
                            data-testid={`nav-${item.id}`}
                        >
                            {item.l}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden sm:block">
                        <LanguageToggle />
                    </div>
                    <button
                        onClick={() => jump("order")}
                        className="btn btn-dark hidden sm:inline-flex"
                        data-testid="nav-order-cta"
                    >
                        {t("nav.order_cta")}
                    </button>
                    <button
                        aria-label="Menu"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 border border-[#2D2824]"
                        data-testid="nav-mobile-toggle"
                    >
                        <span
                            className={`block w-4 h-px bg-[#2D2824] transition-transform ${
                                open ? "rotate-45 translate-y-[3px]" : ""
                            }`}
                        />
                        <span
                            className={`block w-4 h-px bg-[#2D2824] transition-transform ${
                                open ? "-rotate-45 -translate-y-[3px]" : ""
                            }`}
                        />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="md:hidden border-t border-[#D9D1C7] bg-[#F9F6F0]/95 backdrop-blur-xl overflow-hidden"
                        data-testid="nav-mobile-panel"
                    >
                        <div className="container-x py-6 flex flex-col gap-4">
                            {[
                                { id: "ritual", label: t("nav.menu_ritual") },
                                { id: "benefits", label: t("nav.menu_benefits") },
                                { id: "faq", label: t("nav.menu_faq") },
                                { id: "order", label: t("nav.menu_order") },
                            ].map((it) => (
                                <button
                                    key={it.id}
                                    onClick={() => jump(it.id)}
                                    className="text-left font-serif text-2xl text-[#2D2824]"
                                    data-testid={`nav-mobile-${it.id}`}
                                >
                                    {it.label}
                                </button>
                            ))}
                            <div className="pt-4 border-t border-[#D9D1C7]">
                                <LanguageToggle />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
